/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from "react";

import { useSalvarSimulacao } from "~/pages/Simulators/hooks/useSimulatorsQuery";

import useTipoCondicao from "~/fetchs/useTipoCondicao";
import useHierarquia from "~/fetchs/useHierarquia";
import useSimulacao from "~/fetchs/useSimulacao";

// ========== Funções Auxiliares ==========

/**
 * Processa prazos de pagamento a partir de grupos de produtos
 * @param {Array} gruposProduto - Array de grupos de produtos
 * @returns {Array} Array de prazos processados
 */
function processPrazos(gruposProduto) {
  const prazosListing = [];

  for (const grupoProduto of gruposProduto || []) {
    for (const produto of grupoProduto?.produtos || []) {
      const existingPrazo = prazosListing.find(
        (a) => a.grupoProdutoId == (grupoProduto.grupoProdutoId ?? grupoProduto.grupoMaterialId)
      );

      if (existingPrazo && produto.prazoPagamentoId) {
        existingPrazo.prazosPagamentoId.push(produto.prazoPagamentoId);
      } else {
        prazosListing.push({
          grupoProdutoId: grupoProduto.grupoProdutoId ?? grupoProduto.grupoMaterialId,
          prazosPagamentoId: [produto.prazoPagamentoId]
        });
      }
    }
  }

  return prazosListing;
}

/**
 * Obtém o desconto mínimo entre CIF e FOB
 * @param {Object} desconto - Objeto com descontos CIF e FOB
 * @returns {number} Desconto mínimo
 */
function getMinDiscount(desconto) {
  return Math.min(desconto.cif, desconto.fob);
}

/**
 * Processa descontos máximos a partir de grupos de produtos
 * @param {Array} gruposProduto - Array de grupos de produtos
 * @returns {Array} Array de descontos máximos
 */
function processDescontosMaximo(gruposProduto) {
  const biggers = [];

  for (const grupoProduto of gruposProduto || []) {
    for (const produto of grupoProduto.produtos || []) {
      const existing = biggers.find(
        (a) => a.grupoProdutoId == (grupoProduto.grupoProdutoId ?? grupoProduto.grupoMaterialId)
      );

      if (existing) {
        existing.descontos.push(getMinDiscount(produto.desconto));
      } else {
        biggers.push({
          grupoProdutoId: grupoProduto.grupoProdutoId ?? grupoProduto.grupoMaterialId,
          descontos: [getMinDiscount(produto.desconto)],
        });
      }
    }
  }

  return biggers;
}

/**
 * Mapeia um produto para objeto de simulação
 * @param {Object} obj - Objeto do produto
 * @param {Array} tipoCondicoes - Array de tipos de condição
 * @returns {Object} Objeto mapeado
 */
function mapProductToSimulacao(obj, tipoCondicoes) {
  return {
    produtoId: obj.produtoId,
    filialId: obj.filialId,
    tipoCondicaoId: obj.tipoCondicaoId ?? (obj.condicao
      ? tipoCondicoes.find((e) => e.nome.toLowerCase() == obj.condicao.toLowerCase())?.id || null
      : null),
    precoRegraCif: obj.precoRegra.cif ?? 0,
    descontoCif: obj.desconto.cif ?? 0,
    precoFinalCif: obj.precoFinal.cif ?? 0,
    precoRegraFob: obj.precoRegra.fob ?? 0,
    descontoFob: obj.desconto.fob ?? 0,
    precoFinalFob: obj.precoFinal.fob ?? 0,
    quantidade: Number.parseInt(obj.quantidade, 10),
    prazoPagamentoCifId: obj.prazoPagamentoId == 0 ? null : obj.prazoPagamentoId,
    prazoPagamentoFobId: obj.prazoPagamentoId == 0 ? null : obj.prazoPagamentoId,
    id: obj.id ?? 0,
    enviadoAprovacao: obj.enviadoAprovacao,
    grupoMaterialReplicacaoId: obj.grupoMaterialReplicacaoId,
    familiaMaterialReplicacaoId: obj.familiaMaterialReplicacaoId,
    produtoReplicacaoId: obj.produtoReplicacaoId,
    grupoCentralizadorReplicacaoId: obj.grupoCentralizadorReplicacaoId,
    enviaPrazoCif: obj.enviaPrazoCif,
    enviaPrazoFob: obj.enviaPrazoFob,
    fracionamento: obj.fracionamento,
    descargaManual: obj.descargaManual,
  };
}

// ========== Hook Principal ==========

const useSimulation = ({ simulacao, onSuccess, onError }) => {
  const [formValues, setFormValues] = useState({});
  const { tipoCondicoes } = useTipoCondicao(null);



  const { getClienteId } = useSimulacao();

  const prazos = useMemo(() => processPrazos(simulacao?.gruposProduto), [simulacao]);

  const descontosMaximo = useMemo(() => processDescontosMaximo(simulacao?.gruposProduto), [simulacao]);

  const { hierarquiasAprovacaoMaxima } = useHierarquia({
    descontosMaximo,
    prazos
  });

  const simuladorSalvar = useSalvarSimulacao(formValues, onSuccess, onError);

  const handleSubmit = () => {
    const temp = {
      id: simulacao.simulacaoId ?? 0,
      fracionamento: simulacao.fracionamento,
      descargaManual: simulacao.descargaManual,
      clienteId: getClienteId(),
      simulacoesGrupo: simulacao.gruposProduto.map((e) => {
        const apMaximo = hierarquiasAprovacaoMaxima.find(
          (apMaximo) => apMaximo.grupoProdutoId == e.grupoProdutoId
        );

        return {
          grupoMaterialId: e.grupoProdutoId,
          descontoMaximo: apMaximo?.descontoMaximo ?? 0,
          aprovadorMaximoId: null,
          statusSimulacao: e.status,
          id: e.id ?? 0,
          SimulacaoId: simulacao.simulacaoId ?? 0,
          aprovadorMaximoNome: apMaximo?.hierarquia?.nome,
          simulacoesProduto: e.produtos.map((obj) => mapProductToSimulacao(obj, tipoCondicoes)),
        };
      }),
    };
    setFormValues(temp);
  };

  useEffect(() => {
    if (Object.entries(formValues).length > 0) {
      simuladorSalvar();
    }
  }, [formValues]);

  return {
    simulacao,
    hierarquiasAprovacaoMaxima,
    handleSubmit
  };
};

export default useSimulation;
